import React, { useState, useEffect } from 'react';
import { UserCheck, UserX, Users, Download, Search, Trash2, Edit, Clock, CheckCircle, XCircle, AlertCircle, AlertTriangle, PauseCircle, Calendar, X, Save, Lock, ArrowUpDown, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const WORKER_URL = "https://livetv.panguplay.workers.dev";

const PLANS = {
  "30days": { price: 50, validity: 30 },
  "90days": { price: 125, validity: 90 },
  "180days": { price: 250, validity: 180 },
  "365days": { price: 500, validity: 365 }
};

// An approved user is only truly "Active" while their subscription hasn't
// expired yet. Once expiresAt passes, they're effectively "Suspended" -
// still approved in the system, but access should be considered lapsed.
const getEffectiveStatus = (user) => {
  if (user.status !== 'approved') return user.status; // pending | rejected
  if (!user.expiresAt) return 'active';
  return new Date(user.expiresAt).getTime() > Date.now() ? 'active' : 'suspended';
};

const getDaysRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

export default function AdminDashboard() {
  const [apiKey, setApiKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    active: 0,
    suspended: 0,
    rejected: 0,
    expiringSoon: 0,
    revenue: 0
  });
  const [editModal, setEditModal] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchUsers = async () => {
    if (!apiKey) return;

    setLoading(true);
    try {
      const response = await fetch(`${WORKER_URL}/users?key=${apiKey}`);
      const data = await response.json();

      if (response.ok && data.users) {
        setUsers(data.users);
        setIsAuthenticated(true);
        calculateStats(data.users);
        setLastRefreshed(new Date());
      } else {
        alert('Invalid API key or error fetching users');
        setIsAuthenticated(false);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userList) => {
    const pending = userList.filter(u => u.status === 'pending').length;
    const approved = userList.filter(u => u.status === 'approved').length;
    const rejected = userList.filter(u => u.status === 'rejected').length;
    const active = userList.filter(u => getEffectiveStatus(u) === 'active').length;
    const suspended = userList.filter(u => getEffectiveStatus(u) === 'suspended').length;
    const expiringSoon = userList.filter(u => {
      if (getEffectiveStatus(u) !== 'active') return false;
      const days = getDaysRemaining(u.expiresAt);
      return days !== null && days <= 7;
    }).length;
    const revenue = userList
      .filter(u => u.status === 'approved')
      .reduce((sum, u) => sum + (u.amount || 0), 0);

    setStats({
      total: userList.length,
      pending,
      approved,
      active,
      suspended,
      rejected,
      expiringSoon,
      revenue
    });
  };

  const handleApprove = async (uid) => {
    if (!window.confirm('Approve this user?')) return;

    try {
      const response = await fetch(`${WORKER_URL}/approve?uid=${uid}&key=${apiKey}`);
      const data = await response.json();

      if (response.ok) {
        alert('User approved successfully!');
        fetchUsers();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleReject = async (uid) => {
    if (!window.confirm('Reject this user? This will revoke their access immediately.')) return;

    try {
      const response = await fetch(`${WORKER_URL}/reject?uid=${uid}&key=${apiKey}`);
      const data = await response.json();

      if (response.ok) {
        alert('User rejected successfully!');
        fetchUsers();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('Are you sure you want to DELETE this user permanently? This action cannot be undone!')) return;

    try {
      const response = await fetch(`${WORKER_URL}/delete-user?uid=${uid}&key=${apiKey}`);
      const data = await response.json();

      if (response.ok) {
        alert('User deleted successfully!');
        fetchUsers();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

const handleResetPassword = async (uid) => {
  if (!window.confirm('Reset password for this user? They will need to set a new password on next login.')) return;

  try {
    const response = await fetch(`${WORKER_URL}/reset-password?uid=${uid}&key=${apiKey}`);
    const data = await response.json();

    if (response.ok) {
      alert(`Password reset successfully!\n\nInstruction for user:\n"Login with your UID/Mobile and any password to set new password"\n\nUser: ${data.mobile}\nUID: ${data.uid}`);
      fetchUsers();
    } else {
      alert('Error: ' + data.error);
    }
  } catch (error) {
    alert('Error: ' + error.message);
  }
};

  const handleExtendValidity = async () => {
    if (!editModal || !extendDays) return;

    try {
      const response = await fetch(`${WORKER_URL}/extend-validity?uid=${editModal.uid}&days=${extendDays}&key=${apiKey}`);
      const data = await response.json();

      if (response.ok) {
        alert(`Validity extended by ${extendDays} days!`);
        setEditModal(null);
        setExtendDays(30);
        fetchUsers();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

const handleApproveRenewal = async (uid) => {
  if (!window.confirm('Approve this renewal request?')) return;

  try {
    const response = await fetch(`${WORKER_URL}/approve-renewal?uid=${uid}&key=${apiKey}`);
    const data = await response.json();

    if (response.ok) {
      alert(`Renewal approved!\nNew plan: ${data.newPlan}\nNew expiry: ${data.newExpiryIST}`);
      fetchUsers();
    } else {
      alert('Error: ' + data.error);
    }
  } catch (error) {
    alert('Error: ' + error.message);
  }
};

const handleRejectRenewal = async (uid) => {
  if (!window.confirm('Reject this renewal request?')) return;

  try {
    const response = await fetch(`${WORKER_URL}/reject-renewal?uid=${uid}&key=${apiKey}`);
    const data = await response.json();

    if (response.ok) {
      alert('Renewal request rejected!');
      fetchUsers();
    } else {
      alert('Error: ' + data.error);
    }
  } catch (error) {
    alert('Error: ' + error.message);
  }
};

  const handleDownload = async (format) => {
    try {
      const response = await fetch(`${WORKER_URL}/users?download=${format}&key=${apiKey}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-${Date.now()}.${format}`;
      a.click();
    } catch (error) {
      alert('Error downloading: ' + error.message);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' }
    );
  };

  const filteredUsers = users
    .filter(user => {
      if (filter === 'all') return true;
      if (filter === 'active') return getEffectiveStatus(user) === 'active';
      if (filter === 'suspended') return getEffectiveStatus(user) === 'suspended';
      return user.status === filter;
    })
    .filter(user => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        user.mobile?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.transactionId?.toLowerCase().includes(term) ||
        user.uid?.toLowerCase().includes(term) ||
        user.plan?.toLowerCase().includes(term)
      );
    });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal, bVal;
    switch (sortConfig.key) {
      case 'created':
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
      case 'expiry':
        aVal = a.expiresAt ? new Date(a.expiresAt).getTime() : 0;
        bVal = b.expiresAt ? new Date(b.expiresAt).getTime() : 0;
        break;
      case 'amount':
        aVal = a.amount || 0;
        bVal = b.amount || 0;
        break;
      default:
        return 0;
    }
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / itemsPerPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedUsers = sortedUsers.slice(
    (currentPageSafe - 1) * itemsPerPage,
    currentPageSafe * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, itemsPerPage]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const getInitials = (user) => {
    const source = user.email || user.mobile || '?';
    return source.trim().charAt(0).toUpperCase();
  };

  const getStatusBadge = (user) => {
    const effective = getEffectiveStatus(user);
    switch (effective) {
      case 'pending':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-300 flex items-center gap-1 w-fit">
          <Clock className="w-3 h-3" /> Pending
        </span>;
      case 'active': {
        const days = getDaysRemaining(user.expiresAt);
        const soon = days !== null && days <= 7;
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit ${
            soon ? 'bg-orange-500/20 text-orange-300' : 'bg-green-500/20 text-green-300'
          }`}>
            <CheckCircle className="w-3 h-3" /> Active
          </span>
        );
      }
      case 'suspended':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-500/30 text-slate-300 flex items-center gap-1 w-fit">
          <PauseCircle className="w-3 h-3" /> Suspended
        </span>;
      case 'rejected':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-300 flex items-center gap-1 w-fit">
          <XCircle className="w-3 h-3" /> Rejected
        </span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-500/20 text-gray-300 w-fit">Unknown</span>;
    }
  };

  const getExpiryLabel = (user) => {
    if (!user.expiresAt) return null;
    const effective = getEffectiveStatus(user);
    const days = getDaysRemaining(user.expiresAt);

    if (effective === 'suspended') {
      return <span className="text-xs text-red-400 mt-1 block">Expired {Math.abs(days)}d ago</span>;
    }
    if (effective === 'active') {
      const soon = days <= 7;
      return (
        <span className={`text-xs mt-1 block ${soon ? 'text-orange-400 font-semibold' : 'text-purple-400'}`}>
          {days === 0 ? 'Expires today' : `Expires in ${days}d`} · {new Date(user.expiresAt).toLocaleDateString()}
        </span>
      );
    }
    return <span className="text-xs text-purple-400 mt-1 block">Exp: {new Date(user.expiresAt).toLocaleDateString()}</span>;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/5 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full backdrop-blur-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Login</h1>
            <p className="text-purple-300">Enter your API key to access the dashboard</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">API Key</label>
              <input
                type="password"
                placeholder="Enter your admin API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none transition-all"
              />
            </div>

            <button
              onClick={fetchUsers}
              disabled={loading || !apiKey}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50"
            >
              {loading ? 'Authenticating...' : 'Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <header className="border-b border-white/10 backdrop-blur-sm bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">PanguPlay Admin</h1>
                <p className="text-xs text-purple-300">User Management Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {lastRefreshed && (
                <span className="hidden sm:block text-xs text-purple-400">
                  Updated {lastRefreshed.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => setIsAuthenticated(false)}
                className="px-4 py-2 rounded-lg border border-red-500/50 text-red-300 hover:bg-red-500/20 transition-all text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
          <div className="bg-white/5 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-purple-300">Total</span>
              <Users className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-3xl font-bold">{stats.total}</div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-yellow-300">Pending</span>
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-yellow-300">{stats.pending}</div>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-green-300">Active</span>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-green-300">{stats.active}</div>
          </div>

          <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-300">Suspended</span>
              <PauseCircle className="w-5 h-5 text-slate-400" />
            </div>
            <div className="text-3xl font-bold text-slate-300">{stats.suspended}</div>
          </div>

          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-orange-300">Expiring ≤7d</span>
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div className="text-3xl font-bold text-orange-300">{stats.expiringSoon}</div>
          </div>

          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-red-300">Rejected</span>
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-3xl font-bold text-red-300">{stats.rejected}</div>
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-purple-300">Revenue</span>
              <span className="text-purple-400">₹</span>
            </div>
            <div className="text-3xl font-bold text-purple-300">₹{stats.revenue}</div>
          </div>
        </div>

        <div className="bg-white/5 border border-purple-500/30 rounded-2xl backdrop-blur-sm overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    filter === 'all' ? 'bg-purple-500 text-white' : 'bg-white/10 text-purple-300 hover:bg-white/20'
                  }`}
                >
                  All ({stats.total})
                </button>
                <button
                  onClick={() => setFilter('pending')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-white/10 text-purple-300 hover:bg-white/20'
                  }`}
                >
                  Pending ({stats.pending})
                </button>
                <button
                  onClick={() => setFilter('approved')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    filter === 'approved' ? 'bg-green-500 text-white' : 'bg-white/10 text-purple-300 hover:bg-white/20'
                  }`}
                >
                  Approved ({stats.approved})
                </button>
                <button
                  onClick={() => setFilter('active')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    filter === 'active' ? 'bg-emerald-500 text-white' : 'bg-white/10 text-purple-300 hover:bg-white/20'
                  }`}
                >
                  Active ({stats.active})
                </button>
                <button
                  onClick={() => setFilter('suspended')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    filter === 'suspended' ? 'bg-slate-500 text-white' : 'bg-white/10 text-purple-300 hover:bg-white/20'
                  }`}
                >
                  Suspended ({stats.suspended})
                </button>
                <button
                  onClick={() => setFilter('rejected')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    filter === 'rejected' ? 'bg-red-500 text-white' : 'bg-white/10 text-purple-300 hover:bg-white/20'
                  }`}
                >
                  Rejected ({stats.rejected})
                </button>
              </div>

              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 rounded-lg bg-white/10 border border-purple-500/30 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none w-full"
                  />
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" />
                </div>

                <div className="relative group">
                  <button className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 transition-all flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                  <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-purple-500/30 rounded-lg shadow-xl hidden group-hover:block min-w-[120px] z-10">
                    <button onClick={() => handleDownload('csv')} className="block w-full px-4 py-2 text-left hover:bg-purple-500/20 text-sm">CSV</button>
                    <button onClick={() => handleDownload('json')} className="block w-full px-4 py-2 text-left hover:bg-purple-500/20 text-sm">JSON</button>
                    <button onClick={() => handleDownload('txt')} className="block w-full px-4 py-2 text-left hover:bg-purple-500/20 text-sm">TXT</button>
                  </div>
                </div>

                <button
                  onClick={fetchUsers}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
<thead className="bg-white/5 border-b border-white/10">
  <tr>
    <th className="px-6 py-4 text-left text-xs font-semibold text-purple-300 uppercase">User</th>
    <th
      className="px-6 py-4 text-left text-xs font-semibold text-purple-300 uppercase cursor-pointer select-none hover:text-white transition-colors"
      onClick={() => handleSort('amount')}
    >
      <div className="flex items-center gap-1">Plan <SortIcon column="amount" /></div>
    </th>
    <th className="px-6 py-4 text-left text-xs font-semibold text-purple-300 uppercase">Payment</th>
    <th
      className="px-6 py-4 text-left text-xs font-semibold text-purple-300 uppercase cursor-pointer select-none hover:text-white transition-colors"
      onClick={() => handleSort('expiry')}
    >
      <div className="flex items-center gap-1">Status <SortIcon column="expiry" /></div>
    </th>
    <th
      className="px-6 py-4 text-left text-xs font-semibold text-purple-300 uppercase cursor-pointer select-none hover:text-white transition-colors"
      onClick={() => handleSort('created')}
    >
      <div className="flex items-center gap-1">Date <SortIcon column="created" /></div>
    </th>
    <th className="px-6 py-4 text-left text-xs font-semibold text-purple-300 uppercase">Renewal/Extension</th>
    <th className="px-6 py-4 text-right text-xs font-semibold text-purple-300 uppercase">Actions</th>
  </tr>
</thead>

<tbody>
  {paginatedUsers.length === 0 ? (
    <tr>
      <td colSpan="7" className="px-6 py-12 text-center text-purple-400">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No users found</p>
      </td>
    </tr>
  ) : (
    paginatedUsers.map((user) => (
      <tr key={user.uid} className="border-b border-white/5 hover:bg-white/5 transition-colors">
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-sm font-bold shrink-0">
              {getInitials(user)}
            </div>
            <div>
              <div className="text-sm font-semibold">{user.mobile}</div>
              <div className="text-xs text-purple-400">{user.email}</div>
              <div className="text-xs text-purple-500 font-mono">{user.uid}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-semibold">{user.plan}</div>
          <div className="text-xs text-purple-400">₹{user.amount}</div>
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-mono">{user.transactionId}</div>
          <div className="text-xs text-purple-400">{user.mop}</div>
        </td>
        <td className="px-6 py-4">
          {getStatusBadge(user)}
          {getExpiryLabel(user)}
        </td>
        <td className="px-6 py-4">
          <div className="text-sm">{new Date(user.createdAt).toLocaleDateString()}</div>
          <div className="text-xs text-purple-400">{new Date(user.createdAt).toLocaleTimeString()}</div>
        </td>

{/* RENEWAL/EXTENSION COLUMN */}
<td className="px-6 py-4">
  <div className="space-y-1">
    {/* Pending Renewal Request */}
    {user.renewalRequest && user.renewalRequest.status === 'pending' && (
      <div className="text-xs">
        <div className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 mb-1 font-semibold">
          🔄 Renewal Pending
        </div>
        <div className="text-purple-400">
          Plan: {user.renewalRequest.newPlan}
        </div>
        <div className="text-purple-400">
          ₹{user.renewalRequest.amount}
        </div>
        <div className="text-purple-500 font-mono text-[10px]">
          {user.renewalRequest.transactionId}
        </div>
      </div>
    )}

    {/* Approved Renewal (show last renewal info) */}
    {user.renewalRequest && user.renewalRequest.status === 'approved' && (
      <div className="text-xs">
        <div className="px-2 py-1 rounded bg-green-500/20 text-green-300 mb-1 font-semibold">
          ✓ Renewed
        </div>
        <div className="text-purple-400">
          Plan: {user.renewalRequest.newPlan}
        </div>
        <div className="text-purple-400">
          ₹{user.renewalRequest.amount}
        </div>
        <div className="text-purple-500 text-[10px]">
          {new Date(user.renewalRequest.approvedAt).toLocaleDateString()}
        </div>
      </div>
    )}

    {/* Rejected Renewal */}
    {user.renewalRequest && user.renewalRequest.status === 'rejected' && (
      <div className="text-xs">
        <div className="px-2 py-1 rounded bg-red-500/20 text-red-300 mb-1 font-semibold">
          ✗ Renewal Rejected
        </div>
        <div className="text-purple-500 text-[10px]">
          {new Date(user.renewalRequest.rejectedAt).toLocaleDateString()}
        </div>
      </div>
    )}

    {/* Extension History (show last extension) */}
    {user.extensionHistory && user.extensionHistory.length > 0 && (
      <div className="text-xs mt-1 pt-1 border-t border-purple-500/20">
        <div className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 mb-1 font-semibold">
          📅 Extended
        </div>
        <div className="text-purple-400">
          +{user.extensionHistory[user.extensionHistory.length - 1].days} days
        </div>
        <div className="text-purple-500 text-[10px]">
          {new Date(user.extensionHistory[user.extensionHistory.length - 1].extendedAt).toLocaleDateString()}
        </div>
        {user.extensionHistory.length > 1 && (
          <div className="text-purple-600 text-[10px]">
            ({user.extensionHistory.length} times)
          </div>
        )}
      </div>
    )}

    {/* No renewal or extension */}
    {!user.renewalRequest && (!user.extensionHistory || user.extensionHistory.length === 0) && (
      <div className="text-xs text-purple-600">
        -
      </div>
    )}
  </div>
</td>

        <td className="px-6 py-4 text-right">
          <div className="flex gap-2 justify-end flex-wrap">
            {user.status === 'pending' && !user.renewalRequest && (
              <>
                <button
                  onClick={() => handleApprove(user.uid)}
                  className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 transition-all"
                  title="Approve"
                >
                  <UserCheck className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleReject(user.uid)}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all"
                  title="Reject"
                >
                  <UserX className="w-4 h-4" />
                </button>
              </>
            )}

            {/* NEW RENEWAL APPROVAL BUTTONS */}
            {user.renewalRequest && user.renewalRequest.status === 'pending' && (
              <>
                <button
                  onClick={() => handleApproveRenewal(user.uid)}
                  className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 transition-all"
                  title="Approve Renewal"
                >
                  <UserCheck className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleRejectRenewal(user.uid)}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all"
                  title="Reject Renewal"
                >
                  <UserX className="w-4 h-4" />
                </button>
              </>
            )}

{user.status === 'approved' && (
  <button
    onClick={() => setEditModal(user)}
    className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition-all"
    title="Extend Validity"
  >
    <Edit className="w-4 h-4" />
  </button>
)}
            <button
              onClick={() => handleResetPassword(user.uid)}
              className="p-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 transition-all"
              title="Reset Password"
            >
              <Lock className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(user.uid)}
              className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all"
              title="Delete User"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    ))
  )}
</tbody>
            </table>
          </div>

          {sortedUsers.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-white/10">
              <div className="text-sm text-purple-400">
                Showing {(currentPageSafe - 1) * itemsPerPage + 1}-
                {Math.min(currentPageSafe * itemsPerPage, sortedUsers.length)} of {sortedUsers.length}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  style={{ colorScheme: 'dark' }}
                  className="bg-slate-800 text-white border border-purple-500/30 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-purple-400"
                >
                  <option value={10} className="bg-slate-800 text-white">10 / page</option>
                  <option value={25} className="bg-slate-800 text-white">25 / page</option>
                  <option value={50} className="bg-slate-800 text-white">50 / page</option>
                  <option value={100} className="bg-slate-800 text-white">100 / page</option>
                </select>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPageSafe === 1}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm px-2 min-w-[70px] text-center">
                  Page {currentPageSafe} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPageSafe === totalPages}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-900 border border-purple-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Calendar className="w-6 h-6" />
                Extend Validity
              </h3>
              <button onClick={() => setEditModal(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-white/5">
                <div className="text-sm text-purple-300 mb-1">User</div>
                <div className="font-semibold">{editModal.mobile}</div>
                <div className="text-xs text-purple-400">{editModal.email}</div>
              </div>

              <div className="p-4 rounded-lg bg-white/5">
                <div className="text-sm text-purple-300 mb-1">Current Expiry</div>
                <div className="font-semibold">
                  {editModal.expiresAt ? new Date(editModal.expiresAt).toLocaleString() : 'N/A'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Extend by (days)</label>
                <input
                  type="number"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value))}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 text-white focus:border-purple-400 focus:outline-none"
                  min="1"
                  max="365"
                />
                <div className="text-xs text-purple-400 mt-2">
                  New expiry: {new Date(new Date(editModal.expiresAt).getTime() + (extendDays * 24 * 60 * 60 * 1000)).toLocaleString()}
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setEditModal(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-purple-500 hover:bg-purple-500/20 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendValidity}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:shadow-lg hover:shadow-green-500/50 font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
